<template>
  <zg-transition-fade>
    <div
      v-if="visible"
      role="dialog"
      aria-labelledby="zg-modal-title"
      aria-describedby="zg-modal-text"
      :class="['zg-modal', {
        'zg-modal--fullscreen': fullscreen,
        'zg-modal--with-buttons': hasSlot('buttons')
      }]"
    >
      <div
        class="overlay"
        @click="close"
      />

      <div class="box">
        <zg-close-button
          v-if="showClose"
          class="close"
          @click.native="close"
        />

        <div class="content">
          <div
            v-if="hasSlot('image')"
            class="image"
          >
            <slot name="image" />
          </div>

          <h3
            v-if="hasSlot('title')"
            id="zg-modal-title"
            class="title"
          >
            <slot name="title" />
          </h3>

          <div
            v-if="hasSlot('text')"
            id="zg-modal-text"
            class="text"
          >
            <slot name="text" />
          </div>
        </div>

        <div
          v-if="hasSlot('buttons')"
          class="buttons"
        >
          <slot name="buttons" />
        </div>
      </div>
    </div>
  </zg-transition-fade>
</template>

<script>
  import ZgCloseButton from '../ZgCloseButton/ZgCloseButton'
  import ZgTransitionFade from '../ZgTransitionFade/ZgTransitionFade.vue'

  export default {
    name: 'ZgModal',
    components: {
      ZgTransitionFade,
      ZgCloseButton
    },
    props: {
      showClose: {
        type: Boolean,
        default: true
      },
      fullscreen: {
        type: Boolean,
        default: false
      },
      visible: {
        type: Boolean,
        default: false
      }
    },
    data: () => ({
      modalFallbackElement: null
    }),
    mounted () {
      this.modalFallbackElement = document.activeElement
      window.addEventListener('keyup', this.addEscapeCloseListener)
    },
    destroyed () {
      window.removeEventListener('keyup', this.addEscapeCloseListener)
      if (this.modalFallbackElement) {
        this.$nextTick(this.focusModalOpener)
      }
    },
    methods: {
      close () {
        if (!this.visible) return
        this.$emit('close')
      },
      hasSlot (name) {
        return this.$slots && !!this.$slots[name]
      },
      addEscapeCloseListener (e) {
        if (e.keyCode === 27) this.close()
      },
      focusModalOpener () {
        const fallbackElem = document.getElementById(this.modalFallbackElement)
        if (fallbackElem) fallbackElem.focus()
      }
    }
  }
</script>

<style lang="scss" scoped>
  .zg-modal {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;

    .overlay {
      position: fixed;
      inset: 0;
      background-color: rgb(0 0 0 / 20%);
      content: '';
    }

    .box {
      position: relative;
      display: flex;
      flex-direction: column;
      border-radius: radius(box);
      background-color: color(beige);
      margin: rhythm();
      z-index: +1;

      @include mq(large) {
        max-width: $basic-modal-max-size;
        margin: 0;
      }
    }

    .close {
      position: absolute;
      right: rhythm(small);
      top: rhythm(small);
      background-color: color(beige);
      border-radius: 100%;
      z-index: +2;
    }

    .title {
      @include type-title-s;

      margin: 0;

      @include mq(large) {
        @include type-title-m;

        margin: 0;
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      gap: rem(24px);
      padding: rem(24px);
      border-radius: radius(box);
      overflow-y: auto;
    }

    .text {
      ::v-deep p:last-child {
        margin: 0;
      }
    }

    .buttons {
      display: flex;
      flex-direction: column;
      gap: rhythm(xsmall);
      padding: rem(24px);

      @include mq(large) {
        flex-direction: row;
        justify-content: flex-end;
        margin-top: auto;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------

  .zg-modal--fullscreen {
    .box {
      position: absolute;
      inset: 0;
      margin: 0;
      border-radius: 0;

      @include mq(large) {
        top: 2.5%;
        bottom: 2.5%;
        left: 10%;
        right: 10%;
        max-width: none;
        border-radius: radius(box);
      }
    }

    .title {
      @include type-title-m;

      margin: 0;

      @include mq(large) {
        @include type-title-l;

        margin: 0;
      }
    }
  }

  .zg-modal--with-buttons {
    .content {
      padding-bottom: 0;
    }
  }
</style>
